import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState } from 'react';
import Layout from './Layout';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { AlertCircle, CheckCircle, Upload, Loader2 } from 'lucide-react';
import axios from 'axios';
import { API } from '../utils/config';


const DataImport = () => {
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleImport = async () => {
    if (!window.confirm('⚠️ ATTENZIONE: Questa operazione sostituirà tutti i dati nel database.\n\nSei sicuro di voler procedere?')) {
      return;
    }

    setImporting(true);
    setResult(null);
    setError(null);

    try {
      const token = localStorage.getItem('hw_token');
      
      // Step 1: Export from development (if on localhost) or use empty import
      let exportData = null;
      
      // Check if we're on development
      if (window.location.hostname === 'localhost' || window.location.hostname.includes('preview.emergentagent')) {
        // On development - export current data
        const exportResponse = await axios.get(
          `${API}/api/admin/export-database`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        exportData = exportResponse.data;
      }
      
      // Step 2: Import data
      const response = await axios.post(
        `${API}/api/admin/import-production-data`,
        exportData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setResult(response.data);
      setImporting(false);
    } catch (err) {
      console.error('Import error:', err);
      setError(err.response?.data?.detail || 'Errore durante l\'import');
      setImporting(false);
    }
  };

  return (
    <Layout>
      <div className="p-6 max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-6">Importa Dati in Produzione</h1>

        <Card className="p-6 mb-6">
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-yellow-900 mb-2">⚠️ Attenzione</h3>
                <p className="text-sm text-yellow-800">
                  Questa operazione importerà tutti i dati dal file di export nel database di produzione.
                  <br />
                  Tutti i dati esistenti verranno sostituiti.
                  <br />
                  <strong>Esegui questa operazione UNA SOLA VOLTA dopo il primo deploy.</strong>
                </p>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-3">📦 Cosa verrà importato:</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>✅ 695 Immobili</li>
                <li>✅ 205 Responsabili</li>
                <li>✅ 167 Partner</li>
                <li>✅ 398 Prenotazioni</li>
                <li>✅ 114 Utenti</li>
                <li>✅ Tutte le configurazioni e dati mancanti</li>
              </ul>
            </div>

            <Button
              onClick={handleImport}
              disabled={importing}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg"
            >
              {importing ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Importazione in corso...
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5 mr-2" />
                  Importa Dati in Produzione
                </>
              )}
            </Button>

            {importing && (
              <div className="text-center text-gray-600">
                <p>⏳ Attendere circa 30-60 secondi...</p>
                <p className="text-sm mt-2">Non chiudere questa pagina</p>
              </div>
            )}
          </div>
        </Card>

        {/* Risultato Import */}
        {result && (
          <Card className="p-6 border-green-200 bg-green-50">
            <div className="flex items-start gap-3 mb-4">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-green-900 text-lg mb-2">
                  ✅ Import Completato con Successo!
                </h3>
                <p className="text-green-800 mb-4">
                  Tutti i dati sono stati importati correttamente nel database di produzione.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 mb-4">
              <h4 className="font-semibold mb-3">📊 Statistiche Import:</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Collections importate:</span>
                  <span className="font-bold ml-2">{result.stats?.collections_imported}</span>
                </div>
                <div>
                  <span className="text-gray-600">Documenti importati:</span>
                  <span className="font-bold ml-2">{result.stats?.documents_imported}</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-4">
              <h4 className="font-semibold mb-3">✅ Verifica Dati:</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">🎫 Prenotazioni:</span>
                  <span className="font-bold">{result.verification?.bookings}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">🏠 Immobili:</span>
                  <span className="font-bold">{result.verification?.properties}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">👥 Responsabili:</span>
                  <span className="font-bold">{result.verification?.property_managers}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">🤝 Partner:</span>
                  <span className="font-bold">{result.verification?.partners}</span>
                </div>
              </div>
            </div>

            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>🎉 Prossimo passo:</strong> Ricarica l'applicazione o naviga alle pagine Prenotazioni, Immobili, ecc. per vedere tutti i dati!
              </p>
            </div>
          </Card>
        )}

        {/* Errore */}
        {error && (
          <Card className="p-6 border-red-200 bg-red-50">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-red-900 text-lg mb-2">❌ Errore Import</h3>
                <p className="text-red-800">{error}</p>
                <p className="text-sm text-red-700 mt-2">
                  Controlla di essere loggato come Admin e riprova.
                </p>
              </div>
            </div>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default DataImport;
